// New ground: Komoot routes and rides to Komoot highlights, ranked for a given day the same way
// as the library ideas, with two extra rules. Getting to a route's start counts (he has no car,
// so a loop 6 km away is 12 km longer), and his light rules hold: at the weekend riding into
// the dark is fine (finished by 21:30); on a weekday, lanes he hasn't surveyed are treated as
// unlit, so the whole ride is in daylight, back 40 minutes before sunset. Pure; the data is src/data/explore.ts.
import type { State, Route } from "./types";
import { sunTimes, toMin } from "./sun.ts";
import { dow } from "./dates.ts";
import { wallsFor, clampStart, validStarts } from "./day.ts";
import { movingMin, STOPS_MIN, BUFFER_MIN } from "./ride.ts";
import { windLine, type Wind } from "./ideas.ts";
import { homeOf } from "../data/empty.ts";

export type Explore = {
  id: string; kind: "route" | "spot"; name: string; km: number; gain: number; steep: number;
  startKm: number | null; loop: boolean; out: number; back?: number;
  track: [number, number][]; profile: [number, number][]; url: string; gpx?: string;
  spot?: { name: string; cat: string; url: string; at: [number, number] };
  /** From Komoot's route details, % of the distance: paved (asphalt and paved), main roads (A roads and the like), paths (cycleways and tracks, often through parks). */
  mix?: { paved: number | null; main: number; path: number };
  /**
   * Street lighting from OpenStreetMap, % of the distance in 25 m steps: lit (tagged lit=yes),
   * likely (untagged 20 or 30 mph roads and residential streets, which in the UK come with lights),
   * unknown, unlit (tagged lit=no, or untagged paths, tracks and roads at 50 mph and over).
   * dark: unlit stretches of 200 m or more, as [from km, to km, road names]; gaps: unknown ones of 300 m or more.
   */
  light?: { lit: number; likely: number; unknown: number; unlit: number; dark: [number, number, string[]][]; gaps: [number, number, string[]][]; roads: string[] };
  planned?: boolean;
};
/** Lit enough for a weekday evening, on OpenStreetMap's evidence: no unlit stretch, four fifths lit or 30 mph streets. Kyle's own survey overrides it. */
export const litOnOsm = (e: Explore) => !!e.light && e.light.dark.length === 0 && e.light.unlit <= 2 && e.light.lit + e.light.likely >= 80;
/** How many finds are in src/data/explore.ts, so Rides can count without loading the file (tested). */
export const NEW_GROUND = 39;
/** Main roads above this share are left out of Today's picks; still listed in New ground with the share shown. */
export const MAIN_MAX = 40;
export const isCity = (e: Explore) => e.kind === "route" && e.km < 40;
export type Ground = { e: Explore; total: number; approach: number; start: number; end: number; reasons: string[]; score: number };

const roundUp = (m: number, to = 15) => Math.ceil(m / to) * to;

/** Total distance with the ride to the start and back, when the start is more than a kilometre away. */
export const approachKm = (e: Explore) => (e.startKm != null && e.startKm > 1 ? Math.round(e.startKm * 2 * 10) / 10 : 0);

export function newGround(s: State, date: string, start: string, target: number | null, all: Explore[], wind: Wind | null = null, inLibrary: Route[] = s.routes): Ground[] {
  const { lat, lon } = homeOf(s);
  const sun = sunTimes(lat, lon, date);
  const walls = wallsFor(s, date);
  const out: Ground[] = [];
  for (const e of all) {
    if (inLibrary.some((r) => r.id === e.id)) continue;
    if (e.mix && (e.mix.main > MAIN_MAX || (e.mix.paved != null && e.mix.paved < 95))) continue;
    const approach = approachKm(e), total = Math.round((e.km + approach) * 10) / 10;
    const dur = roundUp(movingMin(total, e.gain) + STOPS_MIN);
    // Weekend: his walls only, the 21:30 finish among them. Weekday: unknown lanes in daylight.
    const weekend = dow(date) === 0 || dow(date) === 6;
    const walled = weekend || litOnOsm(e) ? walls : walls.concat([{ start: 0, end: sun.rise, label: "Before sunrise" }, { start: sun.set - BUFFER_MIN, end: 24 * 60, label: "Sunset" }]);
    if (!validStarts(dur, walled).length) continue;
    const { start: st } = clampStart(toMin(start), dur, walled);
    const reasons: string[] = [];
    let score = 0;
    if (target) {
      const off = (total - target) / target;
      if (Math.abs(off) > 0.25) continue;
      score -= Math.abs(off) * 2;
      reasons.push(Math.abs(off) <= 0.1 ? `Right for the ${Math.round(target)} km the plan asks` : off > 0 ? `${Math.round(total - target)} km over the ${Math.round(target)} the plan asks` : `${Math.round(target - total)} km short of the ${Math.round(target)} the plan asks`);
    }
    if (approach) reasons.push(`Starts ${e.startKm} km away; ${approach} km there and back is in the total`);
    if (e.kind === "spot" && (e.back ?? 0) > 0.6) reasons.push("Out and back on the same roads");
    if (litOnOsm(e)) { reasons.push(`Lit on OpenStreetMap: ${e.light!.lit}% tagged lit, ${e.light!.likely}% 30 mph streets, ${e.light!.unknown}% not known`); score += 0.05; }
    else if (e.light && e.light.dark.length) reasons.push(`${Math.round(e.light.dark.reduce((a, d) => a + d[1] - d[0], 0) * 10) / 10} km unlit on OpenStreetMap, from ${e.light.dark[0][0]} km`);
    if (e.mix) { reasons.push(`Main roads ${Math.round(e.mix.main)}%, paths ${Math.round(e.mix.path)}%${e.mix.paved != null ? `, ${Math.round(e.mix.paved)}% tarmac` : ""}`); score -= (e.mix.main / 100) * 0.6; }
    if (e.gain / total >= 12) { reasons.push(`${e.gain.toLocaleString("en-GB")} m of climbing, the kind Day 1 asks for`); score += 0.1; }
    const w = windLine(e.out, wind);
    if (w) { reasons.push(w.line); score += w.plus; }
    if (e.kind === "spot") score += 0.05;
    out.push({ e, total, approach, start: st, end: st + dur, reasons, score });
  }
  return out.sort((a, b) => b.score - a.score);
}
